import React, { useEffect, useState } from 'react';
import EventItem from './EventItem';
import LoadingText from '../text/LoadingText';
import { getApiAxiosInstance } from '../../utils/network';
import { getBatchScoreUpdateRequestStatus } from '../../api';

import styles from './resultList.scss';

const ResultsList = (props) => {
    const {
        eligibleResults,
        results,
        gameType,
        auth,
        showVerified,
        playerId,
        limitResultDisplay,
        showDecimalForOwn,
        isProProfile,
        resultsLoading,
        redirectUrl,
    } = props;

    const [scoreStatusMap, setScoreStatusMap] = useState(new Map());
    const [statusLoading, setStatusLoading] = useState(false);
    const [apiCalled, setApiCalled] = useState(false);

    const subscriptionType = auth && auth.user && auth.user.subscriptionType;

    // Utility function to chunk array into smaller pieces
    const chunkArray = (array, chunkSize) => {
        const chunks = [];
        for (let i = 0; i < array.length; i += chunkSize) {
            chunks.push(array.slice(i, i + chunkSize));
        }
        return chunks;
    };

    // Function to collect all result IDs from all events and draws
    const getAllResultIds = (events) => {
        const allResultIds = [];

        if (!Array.isArray(events) || events.length === 0) {
            return allResultIds;
        }
        events.forEach(event => {
            // Add result IDs directly from the event
            addResultsToList(event?.results, allResultIds);

            // If the event has draws, process each draw
            if (Array.isArray(event?.draws)) {
                event.draws.forEach(draw => {
                    // Add result IDs from each draw
                    addResultsToList(draw?.results, allResultIds);
                });
            }
        });

        return allResultIds;
    };

    // Helper function to push valid result IDs into the provided list
    const addResultsToList = (results, list) => {
        if (!Array.isArray(results)) {
            return;
        }

        results.forEach(result => {
            if (result?.id) {
                list.push(result.id);
            }
        });
    };

    // Batch API call for all result statuses with chunking
    const checkBatchScoreUpdateStatus = async (allResultIds) => {
        if (statusLoading || apiCalled || allResultIds.length === 0) {
            return;
        }

        setStatusLoading(true);
        setApiCalled(true);

        try {
            const BATCH_SIZE = 200; // Match backend limit
            const chunks = chunkArray(allResultIds, BATCH_SIZE);
            const axiosInstance = getApiAxiosInstance();

            // Process all chunks in parallel
            const promises = chunks.map(chunk => getBatchScoreUpdateRequestStatus(axiosInstance, chunk));

            const responses = await Promise.allSettled(promises);

            const statusMap = new Map();

            responses.forEach((response, index) => {
                if (response.status === 'fulfilled' && response.value.data) {
                    if (Array.isArray(response.value.data)) {
                        response.value.data.forEach(statusData => {
                            statusMap.set(statusData.resultId, {
                                status: statusData.status,
                                dataImportType: statusData.dataImportType,
                                importedResult: statusData.importedResult,
                                lastUpdated: statusData.lastUpdated,
                            });
                        });
                    }
                } else if (response.status === 'rejected') {
                    console.error(`Error fetching batch ${index + 1}:`, response.reason);
                } else {
                    console.error(`Unexpected fulfilled response format in batch ${index + 1}:`, response.value);
                }
            });

            setScoreStatusMap(statusMap);
            setStatusLoading(false);
        } catch (error) {
            console.error('Error fetching batch score update status:', error);
            setStatusLoading(false);
            setApiCalled(false);
        }
    };

    // Handle score update request from individual ScoreCard components
    const handleScoreUpdateRequest = (resultId, newStatus) => {
        setScoreStatusMap(prevState => {
            const newStatusMap = new Map(prevState);
            const existingData = newStatusMap.get(resultId) || {};
            newStatusMap.set(resultId, {
                ...existingData,
                status: newStatus,
            });
            return newStatusMap;
        });
    };

    useEffect(() => {
        if (results?.events && results.events.length > 0 && !apiCalled) {
            const allResultIds = getAllResultIds(results.events);
            if (allResultIds.length > 0) {
                checkBatchScoreUpdateStatus(allResultIds);
            }
        }
    }, [results?.events, apiCalled]);

    if (results?.events?.length) {
        return (
            <div>
                {results.events.map((event, i) => {
                    return (
                        <EventItem
                            key={`event-item-${i}`}
                            event={event}
                            gameType={gameType}
                            eligibleResults={eligibleResults}
                            subscriptionType={subscriptionType}
                            showVerified={showVerified}
                            playerId={playerId}
                            limitResultDisplay={limitResultDisplay}
                            showDecimalForOwn={showDecimalForOwn}
                            isProProfile={isProProfile}
                            auth={auth}
                            redirectUrl={redirectUrl}
                            scoreStatusMap={scoreStatusMap}
                            onScoreUpdateRequest={handleScoreUpdateRequest}
                        />
                    );
                })}
            </div>
        );
    }

    if (resultsLoading) {
        return (
            <div>
                <LoadingText text={'Loading Results'} />
            </div>
        );
    }

    return (
        <div className={`${styles.noResultsOuterContainer}`}>
            <div className="row">
                <div className={`col-2 col-md-4`} />
                <div className={`col-8 col-md-4 ${styles.noResultsContainer}`}>
                    <div className={`${styles.noResultsPicture} ${styles.noResultsPlacholder}`}>6-2</div>
                    <div className={`${styles.comingSoon}`}>
                        Scores Coming Soon
                    </div>
                </div>
                <div className={`col-2 col-md-4`} />
            </div>
        </div>
    );
};

export default ResultsList;
